"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Mail, Eye, EyeOff } from "lucide-react";
import { BRAND_INK, ZunoxMark } from "@/components/brand/zunox-mark";
import {
  mensagemDeEntrada,
  SEM_RESPOSTA_DO_SERVIDOR,
  type FrasesDoEcra,
} from "@/lib/mensagens-de-entrada";

/**
 * O 401 é a única resposta que autoriza falar das credenciais. O 400 vem do
 * `zod` da rota a recusar o corpo, e com os dois campos preenchidos (o botão
 * não submete sem eles) o que sobra é o endereço: o `type="email"` do browser
 * deixa passar `ana@zunox`, que o `z.string().email()` recusa.
 */
const FRASES_DO_LOGIN: FrasesDoEcra = {
  credenciais:
    "Credenciais inválidas. Verifique o e-mail e a password e tente novamente.",
  pedidoInvalido:
    "O e-mail não tem um formato válido. Verifique se está completo (por exemplo nome.apelido@empresa.com).",
};

interface UserIdentity {
  id: string;
  full_name: string;
  role: string;
  color: string;
  department_ids: string[];
}

/**
 * O ecrã de entrada. O `export default` é a rota; este export nomeado é o
 * mesmo ecrã com estados iniciais injectáveis, e existe para as storyboards
 * da canvas `tempo/designs/canvases/acesso/login/` poderem mostrar — e deixar
 * editar — os estados que de outra forma só se alcançam com uma resposta do
 * servidor. As props são todas opcionais: a rota continua a montá-lo sem
 * nenhuma.
 */
export function LoginScreen({
  initialEmail = "",
  initialPassword = "",
  initialError = "",
  initialLoading = false,
}: {
  initialEmail?: string;
  initialPassword?: string;
  initialError?: string;
  initialLoading?: boolean;
} = {}) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(initialPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(initialError);
  const [loading, setLoading] = useState(initialLoading);
  const [mounted, setMounted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setLoading(true);
    setError("");

    try {
      // Verify email + password via our secure server-side API
      const res = await fetch("/api/auth/verify-credentials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });

      if (!res.ok) {
        // Houve resposta. Qual é a frase certa para ela está escrito em
        // `lib/mensagens-de-entrada.ts`, que é o mesmo sítio de onde o `/pin`
        // tira as suas — e é um módulo precisamente porque este `else` já
        // mentiu duas vezes, uma vez por ecrã.
        setError(mensagemDeEntrada(res.status, FRASES_DO_LOGIN));
        return;
      }

      const { user }: { user: UserIdentity } = await res.json();

      if (!user) {
        setError(
          "Credenciais inválidas. Verifique o e-mail e a password e tente novamente.",
        );
        return;
      }

      // Persist identity for subsequent auth steps
      sessionStorage.setItem("zunox_user_identity", JSON.stringify(user));
      localStorage.removeItem("zunox_user");
      localStorage.removeItem("session_start");

      if (user.role !== "admin" && (user.department_ids?.length ?? 0) === 0) {
        setError("Sem acesso ao sistema. Contacte o administrador.");
        sessionStorage.removeItem("zunox_user_identity");
        return;
      }

      router.push("/pin");
    } catch {
      // O único caminho em que não houve resposta nenhuma, e por isso o único
      // em que falar da ligação é verdade.
      setError(SEM_RESPOSTA_DO_SERVIDOR);
    } finally {
      setLoading(false);
    }
  };

  // Render neutral shell before hydration
  if (!mounted) {
    return <div className="min-h-screen min-h-dvh bg-background" />;
  }

  const inputStyle = {
    background: "hsl(var(--background))",
    border: "1px solid hsl(var(--border))",
    color: "hsl(var(--foreground))",
    transition: "border-color 0.2s",
  };

  return (
    <div
      /* O degradê de três paragens voltou, agora como `.bg-auth-surface`
         sobre os tokens `--auth-surface-*`. O que o tinha feito sair foi ser
         afinado à mão em hexes, que não acompanhavam o tema; em tokens
         acompanha, e a entrada volta a ser a superfície que o ecrã de
         recuperação nunca deixou de ter. */
      className="min-h-screen min-h-dvh flex items-center justify-center bg-auth-surface"
    >
      {/* A grelha de fundo, que é só do tema escuro. Quem a esconde é o CSS e
          não um `isDark` em JavaScript: no anfitrião das canvases não há
          `ThemeProvider`, e o ramo em JavaScript dava-a por escura também no
          quadro claro. */}
      <div
        className="absolute inset-0 hidden opacity-5 dark:block"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative z-10 w-full max-w-sm px-d4 py-d8">
        {/* Logo */}
        <div className="flex flex-col items-center mb-d6 md:mb-d8">
          <div className="w-16 flex items-center justify-center h-8 mb-1.5">
            {/* O mesmo <ZunoxMark /> que o Style Guide monta. O SVG estava
                aqui inline, o que fazia da marca duas cópias — e a segunda
                nunca acompanharia a primeira.

                A tinta entra por `currentColor` e quem a escolhe é o tema,
                pelo token `--brand-ink`. */}
            <ZunoxMark className="h-full w-full" style={{ color: BRAND_INK }} />
          </div>
          <h1
            className="text-display font-bold tracking-wide leading-[24px]"
            style={{ color: "hsl(var(--foreground))" }}
          >
            Zunox
          </h1>
          <p
            className="text-body mt-d1"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            Business Operating System — v1.0
          </p>
        </div>

        {/* Card */}
        <div
          /* `shadow-e3` em vez de duas sombras afinadas à mão por tema: é o
             degrau de elevação dos diálogos, e o cartão de entrada é
             exactamente isso — a única coisa que flutua sobre a página. */
          className="rounded-panel p-d6 shadow-e3 md:p-d8"
          style={{
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
          }}
        >
          <h2
            className="text-heading font-semibold mb-d1"
            style={{ color: "hsl(var(--foreground))" }}
          >
            Entrar no Sistema
          </h2>
          <p
            className="text-body mb-d5"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            Para aceder introduza as suas credenciais
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-d4">
            {/* Email */}
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome.apelido@empresa.com"
                className="w-full px-d4 rounded-lg placeholder:text-muted-foreground outline-none pr-d10"
                style={{
                  ...inputStyle,
                  /* 16px prevents iOS auto-zoom */
                  fontSize: 16,
                  height: 50,
                }}
                onFocus={(e) => (e.target.style.borderColor = "hsl(var(--ring))")}
                onBlur={(e) =>
                  (e.target.style.borderColor = "hsl(var(--border))")
                }
                autoComplete="email"
                inputMode="email"
              />
              <div
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: "hsl(var(--muted-foreground))" }}
              >
                <Mail size={16} />
              </div>
            </div>

            {/* Password */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-d4 rounded-lg placeholder:text-muted-foreground outline-none pr-d10"
                style={{
                  ...inputStyle,
                  fontSize: 16,
                  height: 50,
                }}
                onFocus={(e) => (e.target.style.borderColor = "hsl(var(--ring))")}
                onBlur={(e) =>
                  (e.target.style.borderColor = "hsl(var(--border))")
                }
                autoComplete="current-password"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  style={{ color: "hsl(var(--muted-foreground))", padding: 4 }}
                  className="hover:opacity-70 transition-opacity tap-target"
                  aria-label={
                    showPassword ? "Ocultar password" : "Mostrar password"
                  }
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-body px-d3 py-2.5 rounded"
                style={{
                  background: "rgba(239,68,68,0.1)",
                  color: "hsl(var(--destructive))",
                  border: "1px solid rgba(239,68,68,0.2)",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email.trim() || !password}
              /* O estado inactivo era um azul apagado por tema, escrito à mão
                 duas vezes. Passa a `--muted`: é a superfície que a aplicação
                 inteira usa para dizer "isto ainda não está disponível", e o
                 `disabled:opacity-50` da classe já lhe dá o resto. */
              className="w-full rounded font-semibold text-primary-foreground transition-all duration-200 disabled:opacity-50 active:scale-[0.98]"
              style={{
                fontSize: 16,
                height: 50,
                background:
                  loading || !email.trim() || !password
                    ? "hsl(var(--muted))"
                    : "hsl(var(--primary))",
              }}
            >
              {loading ? "A verificar..." : "Entrar"}
            </button>
          </form>
        </div>

        <div className="flex flex-col items-center gap-d2 mt-d5">
          <button
            onClick={() => router.push("/admin-recovery")}
            className="transition-colors duration-200 text-body py-d2 px-d4"
            style={{ color: "hsl(var(--muted-foreground))", minHeight: 44 }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = "hsl(var(--foreground))")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = "hsl(var(--muted-foreground))")
            }
          >
            Administrador? Recuperar acesso →
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return <LoginScreen />;
}
